<template>
  <header class="bg-white fixed top-0 w-full shadow-md z-50">
    <div class="mx-auto">
      <nav class="theme-container mx-auto flex items-center justify-between h-11 xl:h-14">
        <!-- Logo -->
        <div class="flex-none">
          <a :href=homeUrl class="flex items-center">
            <img src="/build/images/logo_revrs.svg" alt="Revrs Logo" class="h-[30px] md:h-[35px] w-auto"/>
          </a>
        </div>

        <!-- Desktop Navigation - Centered -->
        <div class="hidden xl:flex items-center justify-center relative 2xl:absolute 2xl:left-1/2 2xl:-translate-x-1/2 flex-1 2xl:flex-none">
          <a :href="homeUrl" class="px-3 py-2 rounded-full text-sm font-semibold text-secondary-500 hover:bg-gray-accent transition duration-150 ease-in-out">
            {{ trans('revrs.userHeader.home') }}
          </a>
          <a :href="howItWorksUrl" class="px-3 py-2 rounded-full text-sm font-semibold text-secondary-500 hover:bg-gray-accent transition duration-150 ease-in-out">
            {{ trans('revrs.userHeader.howItWorks') }}
          </a>
          <a :href="pricesUrl" class="px-3 py-2 rounded-full text-sm font-semibold text-secondary-500 hover:bg-gray-accent transition duration-150 ease-in-out">
            {{ trans('revrs.userHeader.pricing') }}
          </a>
<!--          <a :href="blogUrl" class="px-3 py-2 rounded-full text-sm font-semibold text-secondary-500 hover:bg-gray-accent transition duration-150 ease-in-out">-->
<!--            {{ trans('revrs.userHeader.blog') }}-->
<!--          </a>-->
          <a :href="contactUrl" class="px-3 py-2 rounded-full text-sm font-semibold text-secondary-500 hover:bg-gray-accent transition duration-150 ease-in-out">
            {{ trans('revrs.userHeader.contact') }}
          </a>
        </div>

        <!-- Language switcher and Logout button -->
        <div class="flex items-center space-x-4">
          <!-- Language Switcher -->
          <div class="hidden xl:flex items-center bg-gray-accent rounded-full p-1 w-auto">
            <a :href="englishUrl"
               :class="[
                 'py-1 px-3 text-sm font-semibold rounded-full transition-all duration-300 text-center whitespace-nowrap flex items-center gap-2',
                 currentLang === 'en' 
                   ? 'bg-secondary-400 text-white shadow-lg'
                   : 'text-secondary-500'
               ]"
            >
              {{ trans('revrs.userHeader.english') }}
            </a>
            <a :href="germanUrl"
               :class="[
                 'py-1 px-3 text-sm font-semibold rounded-full transition-all duration-300 text-center whitespace-nowrap flex items-center gap-2',
                 currentLang === 'de' 
                   ? 'bg-secondary-400 text-white shadow-lg'
                   : 'text-secondary-500'
               ]"
            >
              {{ trans('revrs.userHeader.deutsch') }}
            </a>
          </div>
          <a href="https://app.getrevrs.com" class="hidden xl:block px-3 py-2 rounded-full bg-secondary-400 text-sm font-semibold text-white hover:bg-secondary-500 transition duration-150 ease-in-out">
            {{ trans('revrs.userHeader.login') }}
          </a>
        </div>

        <!-- Mobile menu button -->
        <div class="flex items-center xl:hidden gap-3">
          <!-- Language Switcher -->
          <div class="flex items-center bg-gray-accent rounded-full p-1 w-auto">
            <a :href="englishUrl"
               :class="[
                 'flex items-center py-1 px-2 text-sm font-semibold rounded-full transition-all duration-300',
                 currentLang === 'en' 
                   ? 'bg-secondary-400 text-white shadow-lg'
                   : 'text-secondary-500'
               ]"
               style="min-width: 45px;"
            >
              <span>{{ trans('revrs.userHeader.english') }}</span>
            </a>
            <a :href="germanUrl"
               :class="[
                 'flex items-center py-1 px-2 text-sm font-semibold rounded-full transition-all duration-300',
                 currentLang === 'de' 
                   ? 'bg-secondary-400 text-white shadow-lg'
                   : 'text-secondary-500'
               ]"
               style="min-width: 45px;"
            >
              <span>{{ trans('revrs.userHeader.deutsch') }}</span>
            </a>
          </div>
          <button
              @click="isMenuOpen = !isMenuOpen"
              class="inline-flex items-center justify-center rounded-md text-primary-500 hover:bg-gray-accent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-expanded="false"
          >
            <span class="sr-only">{{ trans('revrs.userHeader.openMainMenu') }}</span>
            <div class="relative w-7 h-7">
              <!-- Three line icon -->
              <svg
                  class="absolute transition-all duration-300 ease-in-out text-secondary-500"
                  :class="isMenuOpen ? 'opacity-0 rotate-180 scale-0' : 'opacity-100 rotate-0 scale-100'"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M3 6h18M3 12h18M3 18h18" />
              </svg>
              <!-- X icon -->
              <svg
                  class="absolute transition-all duration-300 ease-in-out text-secondary-500"
                  :class="isMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-180 scale-0'"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 19L19 5M5 5l14 14" />
              </svg>
            </div>
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile menu -->
    <div
        class="xl:hidden container transition-all duration-300 ease-in-out overflow-hidden"
        :class="isMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'"
    >
      <div class="pt-2 pb-3 space-y-1">

        <a @click="isMenuOpen = false"
        :href="homeUrl"
        class="block py-2 rounded-md text-base font-semibold text-secondary-500 hover:bg-gray-accent"
        >
        {{ trans('revrs.userHeader.home') }}
        </a>

        <a @click="isMenuOpen = false"
           :href="howItWorksUrl"
           class="block py-2 rounded-md text-base font-semibold text-secondary-500 hover:bg-gray-accent"
        >
          {{ trans('revrs.userHeader.howItWorks') }}
        </a>

        <a @click="isMenuOpen = false"
           :href="pricesUrl"
           class="block py-2 rounded-md text-base font-semibold text-secondary-500 hover:bg-gray-accent"
        >
          {{ trans('revrs.userHeader.pricing') }}
        </a>

<!--        <a @click="isMenuOpen = false"-->
<!--           :href="blogUrl"-->
<!--           class="block py-2 rounded-md text-base font-semibold text-secondary-500 hover:bg-gray-accent"-->
<!--        >-->
<!--          {{ trans('revrs.userHeader.blog') }}-->
<!--        </a>-->

        <a @click="isMenuOpen = false"
        :href="contactUrl"
        class="block py-2 rounded-md text-base font-semibold text-secondary-500 hover:bg-gray-accent"
        >
        {{ trans('revrs.userHeader.contact') }}
        </a>

        <!-- Language Switcher for Mobile -->
        <div class="items-center justify-center mt-4">
          <a @click="isMenuOpen = false"
             :href="`https://app.getrevrs.com/${transLocale()}/login?action=register`">
            <div class="w-full border border-secondary-400 items-center text-center text-white bg-secondary-400 hover:border-secondary-500 hover:bg-secondary-500 px-10 py-2 transition rounded-full self-center mr-5 font-semibold">
              {{ trans('revrs.userHeader.register') }}
            </div>
          </a>
          <a @click="isMenuOpen = false"
             :href="`https://app.getrevrs.com/${transLocale()}/login`">
            <div class="w-full border-2 border-secondary-400 text-center items-center text-secondary-400 hover:bg-secondary-400 hover:text-white px-10 py-2 transition rounded-full self-center font-semibold mt-3">
              {{ trans('revrs.userHeader.login') }}
            </div>
          </a>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref } from 'vue';
import {trans, transLocale} from "../../js/utilities/revrs-translations";

const props = defineProps({
  homeUrl: String,
  contentProtectionUrl: String,
  howItWorksUrl: String,
  dmcaTakedownUrl: String,
  pricesUrl: String,
  blogUrl: String,
  contactUrl: String,
  faqUrl: String,
  germanUrl: String,
  englishUrl: String
});

const isMenuOpen = ref(false);
const currentLang = ref(document.documentElement.lang || 'en');
</script>